// Round 3, decision 10a (Carla, 2026-10-12): the page the email-change link opens. It NEVER WORKED:
// /verify-email-change was an alias to the Dashboard and nothing read the token, so an address
// could never change. Now this page posts the token to POST /api/auth/verify-email-change and says
// what happened, in the server's words when it refuses (expired, used, another account signed in).
// It works signed in or not (routes/security.js says why), so it is answered by AppWrapper before
// the app's sign-in. The request is made ONCE: a second post would find the link already spent.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { messageFromError } from '../utils/apiError';
import { PageLayout, PageHeader, Card, Banner, Button, ErrorState, HeaderAction, Skeleton } from '../ui';

const TITLES = {
  expired_token: 'This link has expired',
  invalid_token: 'This link can\'t be used',
  missing_token: 'This link is incomplete',
  wrong_account: 'This link is for another account',
  email_taken: 'Your email didn\'t change',
};

export default function VerifyEmailChange() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', []);
  const [state, setState] = useState(token ? { status: 'working' } : { status: 'failed', code: 'missing_token', message: 'There is no code in this link. Open the link from the email again, or ask for a new one in Settings > Security.' });
  const request = useRef(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    if (!request.current) {
      request.current = api('/api/auth/verify-email-change', { method: 'POST', body: JSON.stringify({ token }) })
        .then((res) => res.json());
    }
    request.current.then(
      (data) => { if (alive) setState({ status: 'done', email: data?.email || '' }); },
      (err) => { if (alive) setState({ status: 'failed', code: err?.body?.code || null, message: messageFromError(err, 'Your email could not be changed just now.') }); },
    );
    return () => { alive = false; };
  }, [token, attempt]);

  // Only a failure that changed nothing on the server (it never answered, or it broke) is tried again.
  const retryable = state.status === 'failed' && (!state.code || state.code === 'server_error' || state.code === 'rate_limited');
  const retry = () => { request.current = null; setState({ status: 'working' }); setAttempt((n) => n + 1); };

  return (
    <PageLayout data-page="verify-email-change">
      <PageHeader eyebrow={{ label: 'Account', color: 'pastel-neutral' }} title="Confirm your new email" />
      <Card color="pastel-blue" label="Email address" lift={false}>
        {state.status === 'working' && (
          <div data-testid="vec-working" aria-busy="true">
            <p>Confirming your new address…</p>
            <Skeleton />
          </div>
        )}
        {state.status === 'done' && (
          <div data-testid="vec-done">
            <Banner tone="success">Your Aidey email is now {state.email}.</Banner>
            <p>Use it the next time you sign in. Emails from Aidey go there from now on.</p>
            <div><Button variant="confirm" onClick={() => { window.location.href = '/settings?section=security'; }}>Open Aidey</Button></div>
          </div>
        )}
        {state.status === 'failed' && (
          <div data-testid="vec-failed">
            <ErrorState title={TITLES[state.code] || 'Your email didn\'t change'} message={state.message}
              onRetry={retryable ? retry : undefined} />
            <div><HeaderAction href="/settings?section=security">Go to Settings &gt; Security</HeaderAction></div>
          </div>
        )}
      </Card>
    </PageLayout>
  );
}
